import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputBase,
  Typography
} from '@mui/material'
import { BRAND, BRAND_DARK } from './OrderDetailView'

// Same choices the store app offers when a kitchen accepts an order
const TIMES = [10, 20, 30, 40, 50, 60, 70, 80, 90]

/**
 * Asks for the preparation time (minutes) before an admin accepts an order.
 * The minutes go to the server as `time`, which sets the order's
 * preparationTime exactly like the store app's acceptOrder does.
 * Scheduled orders have no preparation time, so the caller skips the dialog.
 */
const AcceptOrderDialog = ({ open, loading, onClose, onConfirm }) => {
  const { t } = useTranslation()
  const [minutes, setMinutes] = useState(20)

  useEffect(() => {
    if (open) setMinutes(20)
  }, [open])

  const valid = Number.isInteger(minutes) && minutes > 0 && minutes <= 600

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('PreparationTime')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
          {t('PreparationTimeHelp')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1
          }}>
          {TIMES.map(value => (
            <Button
              key={value}
              variant={minutes === value ? 'contained' : 'outlined'}
              disableElevation
              onClick={() => setMinutes(value)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                ...(minutes === value
                  ? { bgcolor: BRAND, '&:hover': { bgcolor: BRAND_DARK } }
                  : { color: 'text.primary', borderColor: 'divider' })
              }}>
              {`${value} ${t('min')}`}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            {t('CustomMinutes')}
          </Typography>
          <InputBase
            type="number"
            value={minutes || ''}
            onChange={e => setMinutes(parseInt(e.target.value, 10) || 0)}
            inputProps={{ min: 1, max: 600 }}
            sx={{
              width: 100,
              px: 1.5,
              height: 38,
              border: '1px solid',
              borderColor: valid ? 'divider' : 'error.main',
              borderRadius: '10px'
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!valid || loading}
          onClick={() => onConfirm(minutes)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '999px',
            px: 3,
            bgcolor: BRAND,
            '&:hover': { bgcolor: BRAND_DARK }
          }}>
          {t('Accept')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AcceptOrderDialog
